import { HOT_THICKNESS_PROFILE_N2371 } from "../ndt/hot-thickness-profile.js";

/**
 * ME — GRUPO DE MATERIAL (AÇO), TEMPERATURA DA MEDIÇÃO E ALTA TEMPERATURA.
 *
 * Dois dados estruturados do ITEM 2 do Relatório de ME:
 *
 *   GRUPO DE MATERIAL   escolhido num catálogo, gravado por código canônico
 *   TEMPERATURA (TME)   inteiro de 0 a 550 °C (máximo do perfil técnico)
 *
 * Abreviações oficiais do documento (MÉTODO A — RESULTADO APROXIMADO):
 *
 *   TME    Temperatura da Medição (= Temperatura da Peça)
 *   Emq    Espessura Medida a Quente               (o que o aparelho indicou)
 *   Vsa    Velocidade Ultrassônica de Referência (m/s), do Procedimento
 *   k      Coeficiente de Variação, do Procedimento
 *   ΔT     Temperatura da Peça − Temperatura de Referência
 *   FC_A   Fator de Correção = (Vsa − k×ΔT) / Vsa
 *   Ea     Espessura Aproximada = Emq × FC_A         — NUNCA "PRECISO"
 *
 * ────────────────────────────────────────────────────────────────────────
 * A CORREÇÃO NÃO MORA MAIS AQUI.
 *
 * Existiu neste arquivo uma matriz "material × faixa de temperatura →
 * coeficiente" (THERMAL_CORRECTION_RULES), vazia por decisão, que produzia
 * "CORREÇÃO TÉRMICA NÃO CONFIGURADA", depois substituída pelo MÉTODO B da
 * PETROBRAS N-2371 (calibração real em bloco-padrão, frio e quente). O
 * MÉTODO B foi REMOVIDO POR DECISÃO EXPLÍCITA DO USUÁRIO (2026) e substituído
 * pelo MÉTODO A — fórmula aproximada, sem calibração: ver
 * `recomputeMeThermalCorrection` em `me-report-domain.ts`. Vsa, k e a
 * Temperatura de Referência vêm SEMPRE do Procedimento (NdtProcedure)
 * vinculado ao relatório — nunca de um valor embutido no código.
 * O limiar de alta temperatura e o máximo da TME vêm do perfil técnico
 * (`src/domain/ndt/hot-thickness-profile.ts`).
 * ────────────────────────────────────────────────────────────────────────
 */

/* ══════════════════════════════════════════════════════════════════════
   1 — FAMÍLIA DE MATERIAL
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Código canônico gravado no relatório. É `string` no registro persistido
 * (um relatório antigo pode trazer um código que saiu do catálogo, e ler um
 * documento emitido nunca pode quebrar), mas tudo o que é OFERECIDO ao
 * usuário vem de `ME_MATERIAL_FAMILIES`.
 */
export type MaterialFamilyCode = string;

export interface MaterialFamily {
  readonly code: MaterialFamilyCode;
  readonly label: string;
  /**
   * Termos alternativos que a busca também aceita. Existem porque o inspetor
   * digita "inox" e não "aço inoxidável", e digitar o nome errado não pode
   * esconder a opção certa.
   */
  readonly aliases: readonly string[];
}

/**
 * Catálogo de famílias. Acrescentar uma família é acrescentar uma linha
 * aqui — o formulário, a busca, o PDF e a validação leem desta lista e não
 * precisam ser tocados.
 */
export const ME_MATERIAL_FAMILIES: readonly MaterialFamily[] = [
  { code: "CARBON_STEEL", label: "AÇO CARBONO", aliases: ["carbono", "carbon steel", "ac", "a36", "astm a106"] },
  { code: "LOW_ALLOY_STEEL", label: "AÇO BAIXA LIGA", aliases: ["baixa liga", "low alloy", "a335", "p1", "c-0,5mo"] },
  { code: "CR_MO_STEEL", label: "AÇO Cr-Mo", aliases: ["cr-mo", "cr mo", "cromo molibdenio", "cromo-molibdênio", "p11", "p22", "p5", "p9", "p91"] },
  { code: "AUSTENITIC_STAINLESS", label: "AÇO INOX AUSTENÍTICO", aliases: ["austenitico", "austenítico", "304", "304l", "316", "316l", "321", "347"] },
  { code: "FERRITIC_STAINLESS", label: "AÇO INOX FERRÍTICO", aliases: ["ferritico", "ferrítico", "405", "409", "430"] },
  { code: "MARTENSITIC_STAINLESS", label: "AÇO INOX MARTENSÍTICO", aliases: ["martensitico", "martensítico", "410", "420"] },
  { code: "DUPLEX", label: "AÇO DUPLEX", aliases: ["duplex", "2205", "uns s31803", "s32205"] },
  { code: "SUPER_DUPLEX", label: "AÇO SUPERDUPLEX", aliases: ["superduplex", "super duplex", "2507", "uns s32750", "s32760"] },
  /*
   * Grupos genéricos do catálogo anterior. Continuam válidos: um relatório
   * já gravado com eles não pode perder o código. Para registro novo,
   * prefira o grupo específico acima.
   */
  { code: "STAINLESS_STEEL", label: "AÇO INOX", aliases: ["inox", "inoxidavel", "inoxidável", "stainless"] },
  { code: "ALLOY_STEEL", label: "AÇO LIGA", aliases: ["liga", "alloy"] }
];

/** "AÇO CARBONO" → "aco carbono". Minúsculas e sem acento, para comparar. */
export function normalizeSearchText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

export function findMaterialFamily(code: string | null | undefined): MaterialFamily | null {
  if (!code) return null;
  return ME_MATERIAL_FAMILIES.find(f => f.code === code) ?? null;
}

/** `true` só para código presente no catálogo — é o gate de "não salvar texto inexistente". */
export function isValidMaterialFamilyCode(code: string | null | undefined): boolean {
  return findMaterialFamily(code) !== null;
}

/**
 * Rótulo para exibir/imprimir. Um código que saiu do catálogo devolve o
 * próprio código em vez de vazio: um relatório antigo continua legível e o
 * valor estranho fica visível, em vez de sumir da folha.
 */
export function materialFamilyLabel(code: string | null | undefined): string {
  if (!code) return "";
  return findMaterialFamily(code)?.label ?? String(code);
}

/**
 * Busca digitada: sem termo devolve o catálogo inteiro; com termo, casa por
 * rótulo, código ou apelido, sem acento e sem caixa.
 */
export function searchMaterialFamilies(query: string): readonly MaterialFamily[] {
  const term = normalizeSearchText(query || "");
  if (!term) return ME_MATERIAL_FAMILIES;
  return ME_MATERIAL_FAMILIES.filter(family => {
    if (normalizeSearchText(family.label).includes(term)) return true;
    if (normalizeSearchText(family.code).includes(term)) return true;
    return family.aliases.some(alias => normalizeSearchText(alias).includes(term));
  });
}

/* ══════════════════════════════════════════════════════════════════════
   2 — TEMPERATURA DA MEDIÇÃO (TME)
   ══════════════════════════════════════════════════════════════════════ */

export const ME_TEMPERATURE_MIN_C = 0;
/** Máximo da TME = máximo do perfil técnico (N-2371: 550 °C). Não é um número do ME. */
export const ME_TEMPERATURE_MAX_C = HOT_THICKNESS_PROFILE_N2371.highTemperatureMaxC;

/**
 * Acima deste valor a medição é tratada como ALTA TEMPERATURA — lido do
 * perfil técnico. O limite é estrito: 60 °C ainda é fluxo convencional,
 * 61 °C já não é.
 */
export const ME_HIGH_TEMPERATURE_THRESHOLD_C = HOT_THICKNESS_PROFILE_N2371.highTemperatureMinC;

/** 0, 1, 2 … 550. Uma lista só, construída uma vez. */
export const ME_TEMPERATURE_OPTIONS: readonly number[] = Object.freeze(
  Array.from({ length: ME_TEMPERATURE_MAX_C - ME_TEMPERATURE_MIN_C + 1 }, (_, i) => ME_TEMPERATURE_MIN_C + i)
);

/** Inteiro dentro de 0…550. `-1` e `551` são recusados; `60,5` também. */
export function isValidMeasurementTemperature(value: unknown): value is number {
  if (typeof value !== "number" || !Number.isFinite(value) || !Number.isInteger(value)) return false;
  return value >= ME_TEMPERATURE_MIN_C && value <= ME_TEMPERATURE_MAX_C;
}

/**
 * Lê a temperatura de um campo. Aceita número e texto ("120", "120 °C",
 * "120,0"), e devolve null para tudo o que não for um inteiro válido da
 * faixa — inclusive para o texto livre que os relatórios antigos guardaram.
 */
export function parseMeasurementTemperature(input: unknown): number | null {
  if (input === null || input === undefined || input === "") return null;
  if (typeof input === "number") return isValidMeasurementTemperature(input) ? input : null;
  if (typeof input !== "string") return null;

  const digits = input.trim().replace(/\s*°?\s*c\s*$/i, "").replace(",", ".").trim();
  if (!/^-?\d+(\.0+)?$/.test(digits)) return null;
  const parsed = Number(digits);
  if (!Number.isFinite(parsed)) return null;
  const rounded = Math.trunc(parsed);
  return isValidMeasurementTemperature(rounded) ? rounded : null;
}

export function formatTemperatureC(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : `${value} °C`;
}

/**
 * Busca digitada na lista de temperaturas: prefixo sobre os dígitos.
 * Digitar "8" traz 8 e 80 … 89; digitar "120" traz só 120.
 * É o que evita rolar 551 linhas à mão.
 */
export function searchTemperatures(query: string): readonly number[] {
  const term = (query || "").replace(/[^\d]/g, "");
  if (!term) return ME_TEMPERATURE_OPTIONS;
  return ME_TEMPERATURE_OPTIONS.filter(value => String(value).startsWith(term));
}

/** A regra do fluxo: acima de 60 °C, e só acima, a medição é de alta temperatura. */
export function isHighTemperatureMeasurement(temperatureC: number | null | undefined): boolean {
  return typeof temperatureC === "number" && Number.isFinite(temperatureC)
    && temperatureC > ME_HIGH_TEMPERATURE_THRESHOLD_C;
}

/**
 * k e Temperatura de Referência do MÉTODO A — FIXOS por decisão oficial do
 * usuário (não vêm mais do Procedimento, nem de nenhum cadastro). Vsa
 * continua vindo do Procedimento (NdtProcedure) vinculado ao relatório —
 * é o único dos três parâmetros que varia por material/aplicação.
 */
export const METHOD_A_COEFFICIENT_K = 1.0;
export const METHOD_A_REFERENCE_TEMPERATURE_C = 30;

/** Legenda impressa sempre que o documento mostra as colunas de alta temperatura. */
export const ME_HIGH_TEMPERATURE_LEGEND = [
  "MÉTODO A — RESULTADO APROXIMADO",
  "TME — Temperatura da Medição (Temperatura da Peça)",
  "Emq — Espessura Medida a Quente",
  "Vsa — Velocidade Ultrassônica de Referência (m/s), do Procedimento",
  "k — Coeficiente de Variação = 1,0 m/s/°C (fixo)",
  "Tref — Temperatura de Referência = 30 °C (fixa)",
  "ΔT — Temperatura da Peça − Temperatura de Referência (30 °C)",
  "FC_A — Fator de Correção = (Vsa − k×ΔT) ÷ Vsa",
  "Ea — Espessura Aproximada = Emq × FC_A"
] as const;

/** FC_A/formatação com 4 casas — a mesma precisão exibida em toda calibração/correção do ME. */
export const formatFactor = (fc: number): string => fc.toFixed(4).replace(".", ",");

/** Formata mm com 2 casas decimais, vírgula decimal — padrão do ME. */
export const formatMm = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return value.toFixed(2).replace(".", ",");
};

/** Rótulo único do MÉTODO A — tela, PDF, AVR e auditoria dizem exatamente o mesmo. */
export const METHOD_A_LABEL = "MÉTODO A — RESULTADO APROXIMADO";

/** Mensagem única de dado ausente — tela, PDF e pendência dizem o mesmo. */
export const METHOD_A_MISSING_DATA_MESSAGE = "MÉTODO A não calculado: faltam dados de origem controlada.";
